import { createEffect, createMemo, createSignal, untrack, type Accessor } from "solid-js"
import { isPlainObjDeeplyEqual } from "../../../isPlainObjDeeplyEqual"
import getTerrainMesh1TileElevationV1 from "../../../three/objects/TerrainMesh1V1/getTerrainMesh1TileElevationV1"
import getTerrainMesh1TileWaterElevationV1 from "../../../three/objects/TerrainMesh1V1/getTerrainMesh1TileWaterElevationV1"
import { WaterProximityKind } from "../../Water/WaterProximityKind.enum"
import { type DirectionDxDy } from "../../direction/DirectionDxDy.type"
import eightDirectionCharsLowerCase from "../../direction/eightDirectionCharsLowerCase"
import eightDirections from "../../direction/eightDirections"
import isEntityLayerIdTerrain1 from "../../entity/EntityLayerId/isEntityLayerIdTerrain1"
import type { Entity } from "../../entity/index"
import isEntityTypeIdWater from "../../entity/isEntityTypeIdWater"
import type { EntityGridXY } from "../../tile_position_xy/EntityGridXY.type"
import type { Engine } from "../Engine.type"
import type { TileGraphNode } from "./TileGraphNode.type"
import createFourCharEdgeCollection from "./createFourCharEdgeCollection"
import createTileGraphNodeWaterElevationCollectionMemo, { type NeighborWaterElevationCollection } from "./createTileGraphNodeWaterElevationCollectionMemo"

type TileGraphNodeWithoutDirections = Omit<TileGraphNode, 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'>

export default function createTileGraphNodeCollection(
  engine: Engine,
  neighborMap: Map<DirectionDxDy, Int16Array>,
  getEntityGridXY: Accessor<EntityGridXY>,
  width: number,
  height: number,
): Array<TileGraphNode> {

  const len = width * height
  const list: Array<TileGraphNode> = new Array(len)

  function createNode(x: number, y: number, index: number): TileGraphNode {
    // returns a non-proxy array of proxy entities
    function getEntsRaw() {
      return getEntityGridXY()[x]?.[y]
    }

    // untrack is important, we do not want getEntityGridXY
    // to be tracked as a dependency of the collection itself.
    const [getEnts, setEnts] = createSignal<Array<Entity>>(untrack(getEntsRaw), {
      equals: isPlainObjDeeplyEqual,
    })

    // createEffect executes after the root function has finished executing
    // so it will create the dependency for the list items
    // but not the list itself
    createEffect(() => {
      setEnts(getEntsRaw())
    })

    const getLayer1Entity = createMemo(() => getEnts().find(isEntityLayerIdTerrain1))
    const getLayer1EntityIdOrNull = createMemo(() => getLayer1Entity()?.id)
    const getLayer1EntityTypeIdOrNull = createMemo(() => getLayer1Entity()?.etype_id)
    ;(getLayer1Entity as TileGraphNode['ents']['l1']).id = getLayer1EntityIdOrNull as TileGraphNode['ents']['l1']['id']
    ;(getLayer1Entity as TileGraphNode['ents']['l1']).etype_id = getLayer1EntityTypeIdOrNull as TileGraphNode['ents']['l1']['etype_id']
    ;(getEnts as TileGraphNode['ents']).l1 = getLayer1Entity as TileGraphNode['ents']['l1']

    const getFloorElevation = createMemo(() => {
      const ent = getLayer1Entity()
      return getTerrainMesh1TileElevationV1(ent)
    })
    const getWaterElevation = createMemo(() => {
      const ent = getLayer1Entity()
      return getTerrainMesh1TileWaterElevationV1(ent)
    })
    const node: TileGraphNodeWithoutDirections = {
      index,
      x,
      y,
      floor_e: getFloorElevation,
      water_e: getWaterElevation,
      ents: getEnts as TileGraphNode['ents'],
      edge: createFourCharEdgeCollection(),
      water_prox: null as unknown as TileGraphNode['water_prox'],
      wec: null as unknown as Accessor<NeighborWaterElevationCollection>,
    }
    return node as TileGraphNode
  }

  // console.log('createTileGraphNodeGridRanges.createList', { width, height }, width * height)
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = x + (y * width)
      list[index] = createNode(x, y, index)
    }
  }

  for (let index = 0; index < list.length; index++) {
    const node = list[index]
    for (let dirIndex = 0; dirIndex < eightDirections.length; dirIndex++) {
      const direction = eightDirections[dirIndex]!
      const char = eightDirectionCharsLowerCase[dirIndex]!
      const directionArray = neighborMap.get(direction)!
      const neighborIndex = directionArray[index]

      node[char] = neighborIndex >= 0 ? list[neighborIndex] : null
    }
  }

  for (let index = 0; index < list.length; index++) {
    const node = list[index]
    node.water_prox = createMemo(() => {
      const etype_id = node.ents.l1.etype_id()
      if (isEntityTypeIdWater(etype_id)) {
        return WaterProximityKind.IsWater
      }
      // const floor_e_value = node.ents.l1.

      const floor_e_value = node.floor_e()
      for (let dirIndex = 0; dirIndex < eightDirections.length; dirIndex++) {
        const char = eightDirectionCharsLowerCase[dirIndex]!
        const neighborNode = (node as TileGraphNode)[char]
        if (neighborNode) {
          const neighborEtypeId = neighborNode.ents.l1.etype_id()
          if (isEntityTypeIdWater(neighborEtypeId)) {
            const water_e_value = neighborNode.water_e()
            if (water_e_value >= floor_e_value) {
              return WaterProximityKind.Adjacent
            }
          }
        }
      }
      for (let dirIndex = 0; dirIndex < eightDirections.length; dirIndex++) {
        const char = eightDirectionCharsLowerCase[dirIndex]!
        const neighborNode = (node as TileGraphNode)[char]
        if (neighborNode) {
          const neighborEtypeId = neighborNode.ents.l1.etype_id()
          if (isEntityTypeIdWater(neighborEtypeId)) {
            return WaterProximityKind.AdjacentRidge
          }
        }
      }
      return WaterProximityKind.None
    })
  }

  for (let index = 0; index < list.length; index++) {
    const node = list[index]
    node.wec = createTileGraphNodeWaterElevationCollectionMemo(engine, node)
  }

  return list
}
